import { Spacing } from '@persona-id/design-tokens';
import Image from 'shared/components/Image';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import IconArrowLeft from 'images/icons/inquiry/nav-back.svg';
import IconX from 'images/icons/inquiry/nav-x.svg';
import IconButton from 'inquiry/components/IconButton';
import { NavbarLogoDisplay } from 'lib/interfaces/ThemeSerialized';
import { Z_INDICES } from 'verify/styles/variables';

const Nav = styled('nav')<{
  styles?: NavbarProps['styles'];
}>`
  position: absolute;
  z-index: ${Z_INDICES.navbar};
  /* Allow clicking elements under navbar */
  pointer-events: none;

  top: 0;
  top: env(safe-area-inset-top);
  left: 0;
  right: 0;

  box-sizing: content-box;
  display: flex;
  flex-wrap: none;
  align-items: center;
  justify-content: space-between;

  height: ${Spacing[5]};
  margin: 0;
  padding: ${SPACINGS.widgetPadding};
  padding-bottom: ${Spacing[4]};
  /* For Apple devices with notches; will be ignored for other devices */
  /* This allows us to use object-fit=cover in the HTML's meta tag */
  /* https://webkit.org/blog/7929/designing-websites-for-iphone-x/ */
  padding-left: calc(${SPACINGS.widgetPadding} + env(safe-area-inset-left));
  padding-right: calc(${SPACINGS.widgetPadding} + env(safe-area-inset-right));
  border-bottom: ${({ styles }) =>
    styles != null ? `${styles.borderWidth} solid ${styles.borderColor}` : ''};
  background-color: ${({ styles }) => styles?.backgroundColor};
`;

const NavOption = styled(IconButton)<{ iconColor?: string | null }>`
  height: ${Spacing[5]};
  width: ${Spacing[5]};
  margin: -${Spacing[1]};
  padding: ${Spacing[1]};

  svg {
    color: ${({ iconColor, theme }) => iconColor ?? theme.colorIconHeader};
  }
`;

const OrganizationLogo = styled(Image)`
  display: block;
  object-fit: contain;
  height: ${({ theme }) => theme.navbarLogoHeight ?? `${Spacing[5]}`};
`;

const LogoContainer = styled.div<{ logoDisplay: NavbarLogoDisplay }>`
  flex: auto;
  display: flex;

  /**
   * Lottie file support:
   * Because we don’t really control the width and height of the logo at the theme level,
   * we limit the max dimensions of the potential svg to 100%
  */
  max-width: 100%;
  max-height: 100%;

  ${({ logoDisplay }) => {
    switch (logoDisplay) {
      case NavbarLogoDisplay.None:
        return css`
          /* maintain spacing to keep close button from collapsing */
          visibility: hidden;
        `;
      case NavbarLogoDisplay.Start:
        return css`
          &:not(:first-child) {
            margin-left: ${Spacing[5]};
          }

          justify-content: flex-start;
        `;
      case NavbarLogoDisplay.End:
        return css`
          &:not(:last-child) {
            margin-right: ${Spacing[5]};
          }

          justify-content: flex-end;
        `;
      case NavbarLogoDisplay.Center:
        return css`
          &:not(:first-child) {
            margin-right: ${Spacing[5]};
          }
          &:not(:last-child) {
            margin-left: ${Spacing[5]};
          }

          justify-content: center;
        `;
    }
  }};
`;

type NavbarProps = {
  onBack: () => void;
  onClose: () => void;
  showBackButton: boolean;
  showCloseButton: boolean;
  colorIconHeader?: string | null;
  styles?: {
    backgroundColor?: string | null;
    borderColor?: string | null;
    borderWidth?: string | null;
  };
  logoContentType?: string | null;
  logoDisplay?: NavbarLogoDisplay;
  logoUrl?: string | null;
  loading?: boolean;
  'data-test'?: string;
  id?: string;
};

const Navbar = ({
  onBack,
  onClose,
  showBackButton,
  showCloseButton,
  styles,
  colorIconHeader,
  logoContentType,
  logoDisplay = NavbarLogoDisplay.None,
  logoUrl,
  loading,
  id,
  'data-test': dataTest = 'navbar',
}: NavbarProps) => {
  return (
    <Nav id={id} data-test={dataTest} styles={styles}>
      {showBackButton && (
        <NavOption
          onClick={onBack}
          id='navbar__back-link'
          data-test='navbar__back-link'
          disabled={loading}
          iconColor={colorIconHeader}
          aria-label='back'
        >
          <IconArrowLeft aria-hidden />
        </NavOption>
      )}

      <LogoContainer logoDisplay={logoDisplay} role='presentation'>
        {logoDisplay !== NavbarLogoDisplay.None && logoUrl != null && (
          <OrganizationLogo
            contentType={logoContentType}
            data-test='navbar__logo'
            alt='logo'
            src={logoUrl}
          />
        )}
      </LogoContainer>

      {showCloseButton && (
        <NavOption
          onClick={onClose}
          id='navbar__close-link'
          data-test='navbar__close-link'
          disabled={loading}
          iconColor={colorIconHeader}
          aria-label='close'
        >
          <IconX aria-hidden />
        </NavOption>
      )}
    </Nav>
  );
};

export default Navbar;
